import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/lib/supabase';

export type Notification = {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  link: string | null;
  read_at: string | null;
  created_at: string;
};

/**
 * Fetches the current user's last 20 notifications, exposes unread count,
 * and subscribes to Realtime INSERT events so the bell badge updates live
 * whenever a trigger fires.
 */
export function useNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ['notifications', user?.id] as const;

  const query = useQuery<Notification[]>({
    queryKey,
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('id, kind, title, body, link, read_at, created_at')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      return data ?? [];
    },
  });

  const unreadCount = query.data?.filter((n) => !n.read_at).length ?? 0;

  /* ---- Live updates ---- */
  useEffect(() => {
    if (!user) return;

    const userId = user.id;
    const channel = supabase
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const fresh = payload.new as Notification;
          queryClient.setQueryData<Notification[]>(
            ['notifications', userId],
            (old = []) => [fresh, ...old],
          );
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]); // stable primitives only — no array refs

  /* ---- Mutations ---- */
  const markOne = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_data, id) => {
      queryClient.setQueryData<Notification[]>(queryKey, (old) =>
        old?.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)),
      );
    },
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      if (!user) return;
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .is('read_at', null);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.setQueryData<Notification[]>(queryKey, (old) =>
        old?.map((n) => ({ ...n, read_at: n.read_at ?? new Date().toISOString() })),
      );
    },
  });

  return {
    notifications: query.data ?? [],
    isLoading: query.isLoading,
    unreadCount,
    markOne: markOne.mutate,
    markAllRead: markAllRead.mutate,
  };
}
